import * as XLSX from 'xlsx';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_PAG_LABELS = { pago: 'Pago', pendente: 'Pendente', isento: 'Isento' };
const fmtStatusPag = (s) => STATUS_PAG_LABELS[s] || (s ? s : 'Pago');

function fmtData(dataStr) {
  if (!dataStr) return '';
  const [y, m, d] = dataStr.split('-');
  return `${d}/${m}/${y}`;
}

function fmtMoeda(val) {
  if (val == null || val === '') return '-';
  return `R$ ${Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
}

function autoWidth(ws, rows) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const widths = keys.map((k) => {
    const maxData = rows.reduce((mx, row) => Math.max(mx, String(row[k] ?? '').length), 0);
    return { wch: Math.min(Math.max(maxData, k.length) + 2, 60) };
  });
  ws['!cols'] = widths;
}

function styledHeader(ws, range, fillColor = '001e45') {
  // aplica cor de fundo e texto branco ao cabeçalho (linha 0)
  for (let C = range.s.c; C <= range.e.c; C++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c: C });
    if (!ws[addr]) continue;
    ws[addr].s = {
      fill: { fgColor: { rgb: fillColor } },
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        bottom: { style: 'thin', color: { rgb: '0147a0' } },
      },
    };
  }
}

function infoSheet(viagem) {
  const data = fmtData(viagem.data);
  const rows = [
    { Campo: 'Título', Valor: viagem.titulo || '' },
    { Campo: 'Origem', Valor: viagem.origem || '' },
    { Campo: 'Destino', Valor: viagem.destino || '' },
    { Campo: 'Data', Valor: data },
    { Campo: 'Horário', Valor: viagem.horario || '' },
    { Campo: 'Veículo', Valor: viagem.veiculo || '' },
    { Campo: 'Motorista', Valor: viagem.motorista || '-' },
    { Campo: 'Capacidade', Valor: viagem.capacidade || 0 },
    { Campo: 'Status', Valor: viagem.status || '' },
  ];
  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [{ wch: 18 }, { wch: 40 }];
  return ws;
}

function downloadXLSX(wb, filename) {
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

// ─── 1. Lista para Motorista ──────────────────────────────────────────────────

export function exportMotoristaXLSX(viagem) {
  const wb = XLSX.utils.book_new();

  // Aba Info
  XLSX.utils.book_append_sheet(wb, infoSheet(viagem), 'Info da Viagem');

  // Aba Passageiros
  const reservas = (viagem.reservas || []).filter((r) => r.status !== 'cancelada');
  const sorted = [...reservas].sort((a, b) => (a.assento || 0) - (b.assento || 0));

  const rows = sorted.map((r) => ({
    Assento: r.assento ?? '',
    Passageiro: r.cliente?.nome || 'Desconhecido',
    'CPF/Documento': r.cliente?.documento || '-',
    Telefone: r.cliente?.telefone || '-',
    Status: r.status === 'confirmada' ? 'Confirmado' : r.status === 'pendente' ? 'Pendente' : r.status,
    'Check-in': r.check_in ? 'Sim' : 'Não',
    'Pagamento': fmtStatusPag(r.status_pagamento),
    'Forma Pagamento': r.forma_pagamento || '-',
    'Local de Embarque': r.local_embarque || '-',
    'Local de Desembarque': r.local_desembarque || '-',
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  autoWidth(ws, rows);
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  styledHeader(ws, range);
  XLSX.utils.book_append_sheet(wb, ws, 'Passageiros');

  downloadXLSX(wb, `lista-motorista-${viagem.titulo || viagem.id}`);
}

// ─── 2. Lista de Bagagem ──────────────────────────────────────────────────────

export function exportBagagemXLSX(viagem, bagagens) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, infoSheet(viagem), 'Info da Viagem');

  const reservas = (viagem.reservas || []).filter((r) => r.status !== 'cancelada');
  const bagByReservaId = {};
  (bagagens || []).forEach((b) => { bagByReservaId[b.reserva_id] = b; });

  const sorted = [...reservas].sort((a, b) => (a.assento || 0) - (b.assento || 0));

  const rows = sorted.map((r) => {
    const b = bagByReservaId[r.id];
    if (b) {
      return {
        Assento: r.assento ?? '',
        Passageiro: r.cliente?.nome || b.passageiro_nome || 'Desconhecido',
        'Nº Malas': b.num_malas ?? 0,
        'Peso Total (kg)': b.peso_total_kg ?? 0,
        'Excesso (kg)': b.excesso_kg ?? 0,
        'Taxa Excesso': b.taxa_excesso > 0 ? fmtMoeda(b.taxa_excesso) : 'Sem excesso',
        'Código Bagagem': b.codigo_bagagem || '-',
        'Status Pagamento': { pendente: 'Pendente', pago: 'Pago', isento: 'Isento' }[b.status_pagamento] || b.status_pagamento,
      };
    }
    return {
      Assento: r.assento ?? '',
      Passageiro: r.cliente?.nome || 'Desconhecido',
      'Nº Malas': 'Não registrado',
      'Peso Total (kg)': '-',
      'Excesso (kg)': '-',
      'Taxa Excesso': '-',
      'Código Bagagem': '-',
      'Status Pagamento': 'A verificar',
    };
  });

  const ws = XLSX.utils.json_to_sheet(rows);
  autoWidth(ws, rows);
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  styledHeader(ws, range);
  XLSX.utils.book_append_sheet(wb, ws, 'Bagagens');

  // Aba Resumo
  const comBagagem = Object.keys(bagByReservaId).length;
  const semBagagem = sorted.length - comBagagem;
  const excessoPendente = (bagagens || []).filter(
    (b) => b.excesso_kg > 0 && b.status_pagamento === 'pendente'
  ).length;
  const resumoRows = [
    { Item: 'Total Passageiros', Quantidade: sorted.length },
    { Item: 'Com Bagagem Registrada', Quantidade: comBagagem },
    { Item: 'Sem Bagagem Registrada', Quantidade: semBagagem },
    { Item: 'Excesso Pendente de Pagamento', Quantidade: excessoPendente },
  ];
  const wsResumo = XLSX.utils.json_to_sheet(resumoRows);
  wsResumo['!cols'] = [{ wch: 32 }, { wch: 16 }];
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo');

  downloadXLSX(wb, `lista-bagagem-${viagem.titulo || viagem.id}`);
}

// ─── 3. Lista de Encomendas ───────────────────────────────────────────────────

export function exportEncomendasXLSX(viagem, encomendas) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, infoSheet(viagem), 'Info da Viagem');

  const statusMap = {
    recebida: 'Recebida',
    em_transito: 'Em Trânsito',
    entregue: 'Entregue',
    cancelada: 'Cancelada',
  };

  const rows = (encomendas || []).map((e) => ({
    'Código': e.codigo_interno || e.id?.slice(0, 8).toUpperCase() || '-',
    'Descrição': e.descricao || '-',
    'Remetente': e.remetente_nome || '-',
    'Tel. Remetente': e.remetente_telefone || '-',
    'Destinatário': e.destinatario_nome || '-',
    'Tel. Destinatário': e.destinatario_telefone || '-',
    'Local de Entrega': e.destino || '-',
    'Volumes': e.volumes ?? 1,
    'Valor': e.valor ? fmtMoeda(e.valor) : '-',
    'Forma Pagamento': e.forma_pagamento || '-',
    'Status': statusMap[e.status] || e.status || '-',
  }));

  const ws = rows.length
    ? XLSX.utils.json_to_sheet(rows)
    : XLSX.utils.aoa_to_sheet([['Nenhuma encomenda cadastrada para esta viagem.']]);

  if (rows.length) {
    autoWidth(ws, rows);
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    styledHeader(ws, range);
  }
  XLSX.utils.book_append_sheet(wb, ws, 'Encomendas');

  // Aba Resumo
  const totalValor = (encomendas || []).reduce((s, e) => s + (Number(e.valor) || 0), 0);
  const totalVols = (encomendas || []).reduce((s, e) => s + (Number(e.volumes) || 1), 0);
  const counts = { recebida: 0, em_transito: 0, entregue: 0, cancelada: 0 };
  (encomendas || []).forEach((e) => { if (counts[e.status] != null) counts[e.status]++; });
  const resumoRows = [
    { Item: 'Total de Encomendas', Valor: (encomendas || []).length },
    { Item: 'Total de Volumes', Valor: totalVols },
    { Item: 'Valor Total', Valor: fmtMoeda(totalValor) },
    { Item: 'Recebidas', Valor: counts.recebida },
    { Item: 'Em Trânsito', Valor: counts.em_transito },
    { Item: 'Entregues', Valor: counts.entregue },
    { Item: 'Canceladas', Valor: counts.cancelada },
  ];
  const wsResumo = XLSX.utils.json_to_sheet(resumoRows);
  wsResumo['!cols'] = [{ wch: 28 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo');

  downloadXLSX(wb, `lista-encomendas-${viagem.titulo || viagem.id}`);
}

// ─── 4. Bilhetes de Embarque ──────────────────────────────────────────────────

export function exportEmbarqueXLSX(viagem) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, infoSheet(viagem), 'Info da Viagem');

  const reservas = (viagem.reservas || []).filter((r) => r.status !== 'cancelada');
  const sorted = [...reservas].sort((a, b) => (a.assento || 0) - (b.assento || 0));

  const rows = sorted.map((r) => ({
    Assento: r.assento ?? '',
    Passageiro: r.cliente?.nome || 'Desconhecido',
    'CPF/Documento': r.cliente?.documento || '-',
    Telefone: r.cliente?.telefone || '-',
    Status: r.status === 'confirmada' ? 'Confirmado' : r.status === 'pendente' ? 'Pendente' : r.status,
    'Check-in': r.check_in ? 'Sim' : 'Não',
    'Pagamento': fmtStatusPag(r.status_pagamento),
    'Forma Pagamento': r.forma_pagamento || '-',
    'Local de Embarque': r.local_embarque || '-',
    'Local de Desembarque': r.local_desembarque || '-',
    'ID Reserva': r.id || '-',
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  autoWidth(ws, rows);
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  styledHeader(ws, range);
  XLSX.utils.book_append_sheet(wb, ws, 'Lista de Embarque');

  downloadXLSX(wb, `lista-embarque-${viagem.titulo || viagem.id}`);
}

// ─── 5. Relatório Completo de Passageiros ─────────────────────────────────────

export function exportRelatorioXLSX(viagem) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, infoSheet(viagem), 'Info da Viagem');

  const reservas = (viagem.reservas || []).filter((r) => r.status !== 'cancelada');
  const sorted = [...reservas].sort((a, b) => (a.assento || 0) - (b.assento || 0));
  const receita = reservas.reduce((s, r) => s + (Number(r.valor) || 0), 0);
  const checkins = reservas.filter((r) => r.check_in).length;

  const rows = sorted.map((r) => ({
    Assento: r.assento ?? '',
    Passageiro: r.cliente?.nome || 'Desconhecido',
    'CPF/Documento': r.cliente?.documento || '-',
    Telefone: r.cliente?.telefone || '-',
    Status: r.status === 'confirmada' ? 'Confirmada' : r.status === 'pendente' ? 'Pendente' : r.status,
    Valor: r.valor ? fmtMoeda(r.valor) : '-',
    'Pagamento': fmtStatusPag(r.status_pagamento),
    'Forma Pagamento': r.forma_pagamento || '-',
    'Check-in': r.check_in ? 'Sim' : 'Não',
    Vendedor: r.vendedor || '-',
    'Local de Embarque': r.local_embarque || '-',
    'Local de Desembarque': r.local_desembarque || '-',
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  autoWidth(ws, rows);
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  styledHeader(ws, range);
  XLSX.utils.book_append_sheet(wb, ws, 'Passageiros');

  // Aba Resumo Financeiro
  const pagos = reservas.filter((r) => !r.status_pagamento || r.status_pagamento === 'pago').length;
  const pendentes = reservas.filter((r) => r.status_pagamento === 'pendente').length;
  const isentos = reservas.filter((r) => r.status_pagamento === 'isento').length;
  const receitaPaga = reservas
    .filter((r) => !r.status_pagamento || r.status_pagamento === 'pago')
    .reduce((s, r) => s + (Number(r.valor) || 0), 0);
  const receitaPendente = reservas
    .filter((r) => r.status_pagamento === 'pendente')
    .reduce((s, r) => s + (Number(r.valor) || 0), 0);

  // Agrupa por forma de pagamento
  const porForma = {};
  reservas.forEach((r) => {
    const forma = r.forma_pagamento || 'Não informado';
    porForma[forma] = (porForma[forma] || 0) + 1;
  });

  const resumoRows = [
    { Item: 'Total Assentos (capacidade)', Valor: viagem.capacidade ?? 0 },
    { Item: 'Reservas Confirmadas', Valor: reservas.length },
    { Item: 'Assentos Livres', Valor: viagem.assentos_livres ?? (viagem.capacidade - reservas.length) },
    { Item: 'Check-in Realizado', Valor: checkins },
    { Item: 'Aguardando Check-in', Valor: reservas.length - checkins },
    { Item: '─── Pagamento ───', Valor: '' },
    { Item: 'Passagens Pagas', Valor: pagos },
    { Item: 'Passagens Pendentes', Valor: pendentes },
    { Item: 'Passagens Isentas', Valor: isentos },
    { Item: 'Receita Recebida', Valor: fmtMoeda(receitaPaga) },
    { Item: 'Receita Pendente', Valor: fmtMoeda(receitaPendente) },
    { Item: 'Receita Total', Valor: fmtMoeda(receita) },
    { Item: '─── Formas de Pagamento ───', Valor: '' },
    ...Object.entries(porForma).map(([forma, qtd]) => ({ Item: forma, Valor: qtd })),
  ];
  const wsResumo = XLSX.utils.json_to_sheet(resumoRows);
  wsResumo['!cols'] = [{ wch: 30 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo Financeiro');

  downloadXLSX(wb, `relatorio-passageiros-${viagem.titulo || viagem.id}`);
}
